import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import SharedLayout from '../../components/website/SharedLayout';
import {
  Trophy, Users, BarChart3, Settings, Gift, Shield, Clock, 
  CheckCircle2, ArrowRight, Play, Star, Zap, Target, Award,
  Gamepad2, Brain, Lock, FileText, Scale, ChevronRight,
  Sparkles, TrendingUp, UserCheck, MessageSquare, Calendar
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Images générées pour la page
const HERO_IMAGE = "https://static.prod-images.emergentagent.com/jobs/0ad8004c-c8ae-44ad-a91e-d4d3309161cb/images/eeadb914c8d323570ea8a1050799fd900dade5ea198622863c88cad7bd24d515.png";
const BACKOFFICE_IMAGE = "https://static.prod-images.emergentagent.com/jobs/0ad8004c-c8ae-44ad-a91e-d4d3309161cb/images/f665e92e366f49b86a2fd6bd6c933d2aae4a517a923cbbdd1fcde3e0984727ad.png";
const QUIZ_IMAGE = "https://static.prod-images.emergentagent.com/jobs/0ad8004c-c8ae-44ad-a91e-d4d3309161cb/images/d44cef1f4004772e19fa03a20b670d6e5d25a22acf3164c7b2fdfa19a4aa0696.png";
const WINNER_IMAGE = "https://static.prod-images.emergentagent.com/jobs/0ad8004c-c8ae-44ad-a91e-d4d3309161cb/images/2c347b185cc344032dc58622305a9d11be35f45422a6ce6dba37c4a646d9e6cd.png";

// Fonctionnalités principales
const FEATURES = [
  {
    icon: Brain,
    title: "Quiz personnalisés",
    description: "Questions sur mesure autour de votre métier, vos produits ou votre secteur d'activité",
    color: "from-purple-500 to-violet-600"
  },
  {
    icon: Users,
    title: "Gestion des joueurs",
    description: "Inscription, authentification, profils joueurs et historique de participation",
    color: "from-blue-500 to-cyan-600"
  },
  {
    icon: BarChart3,
    title: "Statistiques en temps réel",
    description: "Tableaux de bord complets avec métriques d'engagement et performances",
    color: "from-green-500 to-emerald-600"
  },
  {
    icon: Trophy,
    title: "Classements & Points",
    description: "Système de scoring dynamique avec leaderboard et récompenses",
    color: "from-amber-500 to-orange-600"
  },
  {
    icon: Gift,
    title: "Tirages au sort",
    description: "Organisation de tirages avec possibilité d'intervention d'un huissier de justice",
    color: "from-pink-500 to-rose-600"
  },
  {
    icon: FileText,
    title: "Règlement juridique",
    description: "Rédaction complète du règlement du jeu conforme à la législation",
    color: "from-slate-500 to-slate-700"
  }
];

// Fonctionnalités détaillées du back-office
const BACKOFFICE_FEATURES = [
  { icon: Settings, label: "Configuration complète des jeux" },
  { icon: Calendar, label: "Planification des quiz mensuels/hebdomadaires" },
  { icon: UserCheck, label: "Validation des inscriptions" },
  { icon: MessageSquare, label: "Communication avec les participants" },
  { icon: TrendingUp, label: "Analyse des performances" },
  { icon: Lock, label: "Sécurité et anti-triche" },
];

// Étude de cas - Le Coffre MSC
const CASE_STUDY = {
  title: "Le Coffre MSC 2026",
  subtitle: "Une expérience de jeu-concours annuelle engageante",
  description: "Découvrez comment nous avons créé un concept de jeu-concours innovant qui fidélise les professionnels tout au long de l'année avec des quiz mensuels et un grand tirage final.",
  stats: [
    { value: "12", label: "Quiz mensuels" },
    { value: "24", label: "Indices à collecter" },
    { value: "1", label: "Coffre à ouvrir" },
    { value: "∞", label: "Engagement" }
  ],
  features: [
    "Jeu annuel avec mécanisme de collecte d'indices",
    "Quiz thématiques mensuels sur les métiers de la communication",
    "Système de points et classement",
    "Grand tirage final le 24 décembre",
    "Gros lot : 24h de création + site vitrine offert",
    "Espace joueur personnalisé avec progression"
  ]
};

// Types de résultats possibles
const RESULT_TYPES = [
  {
    icon: Trophy,
    title: "Classement par points",
    description: "Les meilleurs scores gagnent. Idéal pour récompenser l'engagement et la connaissance.",
    ideal: "Quiz de formation, fidélisation clients"
  },
  {
    icon: Gift,
    title: "Tirage au sort",
    description: "Chaque participant a sa chance. Parfait pour maximiser la participation.",
    ideal: "Lancements produits, événements"
  },
  {
    icon: Scale,
    title: "Tirage par huissier",
    description: "Tirage au sort officiel avec constat d'huissier pour une transparence totale.",
    ideal: "Gros lots, opérations réglementées"
  }
];

const GameContestServicePage = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);

  return (
    <SharedLayout>
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative pt-24 md:pt-32 pb-20 overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="mb-4 bg-gradient-to-r from-orange-500 to-red-500 text-white border-0">
                  <Gamepad2 className="w-3 h-3 mr-1" />
                  Nouveau service Marketing
                </Badge>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                  Jeux Concours <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-red-400">
                    Sur Mesure
                  </span>
                </h1>
                
                <p className="text-lg md:text-xl text-slate-300 mb-8 leading-relaxed">
                  Créez des expériences de jeu-concours <strong className="text-white">dynamiques et engageantes</strong> pour fidéliser vos clients, 
                  former vos équipes ou animer votre communauté. Solution clé en main avec back-office complet.
                </p>
                
                <div className="flex flex-wrap gap-4">
                  <Button 
                    size="lg"
                    className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full px-8"
                    onClick={() => navigate('/site#contact')}
                  >
                    Demander un devis
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <Button 
                    size="lg"
                    variant="outline"
                    className="border-white/30 text-white hover:bg-white/10 rounded-full px-8"
                    onClick={() => document.getElementById('case-study').scrollIntoView({ behavior: 'smooth' })}
                  >
                    <Play className="w-5 h-5 mr-2" />
                    Voir l'étude de cas
                  </Button>
                </div>
                
                {/* Trust badges */}
                <div className="flex items-center gap-6 mt-10 pt-8 border-t border-white/10">
                  <div className="flex items-center gap-2 text-slate-400">
                    <Shield className="w-5 h-5 text-green-400" />
                    <span className="text-sm">100% personnalisable</span>
                  </div>
                  <div className="flex items-center gap-2 text-slate-400">
                    <Clock className="w-5 h-5 text-cyan-400" />
                    <span className="text-sm">Livraison rapide</span>
                  </div>
                </div>
              </div>
              
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                  <img 
                    src={HERO_IMAGE} 
                    alt="Interface de jeu-concours interactif" 
                    className="w-full h-auto"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                </div>
                
                {/* Floating stats */}
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-4 border">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-800">+200%</p>
                      <p className="text-sm text-slate-500">d'engagement</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-orange-100 text-orange-700 border-orange-200">
                Fonctionnalités
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Une solution complète <span className="text-orange-500">clé en main</span>
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                De la conception à la gestion, nous prenons en charge l'intégralité de votre jeu-concours
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {FEATURES.map((feature, idx) => (
                <Card 
                  key={idx}
                  className="group hover:shadow-xl transition-all duration-300 border-0 bg-white overflow-hidden"
                >
                  <CardContent className="p-6">
                    <div className={cn(
                      "w-14 h-14 rounded-2xl bg-gradient-to-r flex items-center justify-center mb-4",
                      "group-hover:scale-110 transition-transform",
                      feature.color
                    )}>
                      <feature.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-800 mb-2">{feature.title}</h3>
                    <p className="text-slate-600">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Case Study - Le Coffre MSC */}
        <section id="case-study" className="py-20 bg-gradient-to-br from-red-900 via-red-800 to-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-white/10 text-white border-white/20">
                <Star className="w-3 h-3 mr-1 text-yellow-400" />
                Étude de cas
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                {CASE_STUDY.title}
              </h2>
              <p className="text-lg text-white/70 max-w-2xl mx-auto">
                {CASE_STUDY.subtitle}
              </p>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Image du Coffre */}
              <div className="relative">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <div className="aspect-video bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center relative">
                    {/* Coffre illustration */}
                    <div className="relative">
                      <div className="w-48 h-48 md:w-64 md:h-64 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-800 border-4 border-red-500/50 flex items-center justify-center shadow-2xl">
                        <Lock className="w-20 h-20 md:w-28 md:h-28 text-red-500" />
                      </div>
                      <div className="absolute -top-3 -right-3 bg-gradient-to-r from-yellow-400 to-amber-500 rounded-full p-2 shadow-lg">
                        <Trophy className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    
                    {/* Floating elements */}
                    <div className="absolute top-4 left-4 bg-red-500/20 backdrop-blur rounded-lg px-3 py-2">
                      <span className="text-red-300 text-sm font-medium">Le Coffre MSC 2026</span>
                    </div>
                    <div className="absolute bottom-4 right-4 bg-white/10 backdrop-blur rounded-lg px-3 py-2">
                      <span className="text-white text-sm">Gros lot : 24h de création</span>
                    </div>
                  </div>
                </div>
                
                {/* Stats cards */}
                <div className="grid grid-cols-4 gap-3 mt-6">
                  {CASE_STUDY.stats.map((stat, idx) => (
                    <div key={idx} className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
                      <p className="text-2xl font-bold text-white">{stat.value}</p>
                      <p className="text-xs text-white/60">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Description */}
              <div>
                <p className="text-lg text-white/80 mb-8 leading-relaxed">
                  {CASE_STUDY.description}
                </p>
                
                <div className="space-y-4">
                  {CASE_STUDY.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <CheckCircle2 className="w-4 h-4 text-white" />
                      </div>
                      <p className="text-white/90">{feature}</p>
                    </div>
                  ))}
                </div>
                
                <div className="mt-10 p-6 bg-white/10 backdrop-blur rounded-xl border border-white/10">
                  <p className="text-white/60 text-sm mb-2">Résultat obtenu</p>
                  <p className="text-xl font-semibold text-white">
                    Engagement mensuel des professionnels avec la marque MSC, 
                    valorisation de l'expertise métier et fidélisation client sur 12 mois.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Back-office Features */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="mb-4 bg-cyan-100 text-cyan-700 border-cyan-200">
                  <Settings className="w-3 h-3 mr-1" />
                  Back-office
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                  Un tableau de bord <span className="text-cyan-500">puissant</span> pour tout gérer
                </h2>
                <p className="text-lg text-slate-600 mb-8">
                  Gardez le contrôle total sur votre jeu-concours grâce à une interface d'administration intuitive et complète.
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {BACKOFFICE_FEATURES.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                      <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                        <feature.icon className="w-5 h-5 text-cyan-600" />
                      </div>
                      <span className="text-slate-700 font-medium text-sm">{feature.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl border border-slate-200">
                  <img 
                    src={BACKOFFICE_IMAGE} 
                    alt="Interface d'administration du jeu-concours" 
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quiz Section */}
        <section className="py-20 bg-gradient-to-br from-purple-900 via-violet-800 to-slate-900 text-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1">
                <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10">
                  <img 
                    src={QUIZ_IMAGE} 
                    alt="Interface de quiz interactif" 
                    className="w-full h-auto"
                  />
                </div>
              </div>
              
              <div className="order-1 lg:order-2">
                <Badge className="mb-4 bg-white/10 text-white border-white/20">
                  <Brain className="w-3 h-3 mr-1" />
                  Quiz interactifs
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Des quiz qui <span className="text-purple-300">captivent</span> vos participants
                </h2>
                <p className="text-lg text-white/80 mb-8">
                  Créez des questions personnalisées autour de votre univers : produits, services, 
                  histoire de l'entreprise, secteur d'activité... Tout est possible !
                </p>
                
                <div className="space-y-4">
                  {[
                    "Questions à choix multiples avec timer",
                    "Niveaux de difficulté personnalisables",
                    "Feedback instantané et explication des réponses",
                    "Système de points et bonus",
                    "Compatible mobile et desktop"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-purple-300" />
                      <span className="text-white/90">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Result Types */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <Badge className="mb-4 bg-amber-100 text-amber-700 border-amber-200">
                <Award className="w-3 h-3 mr-1" />
                Modes de résultats
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4">
                Choisissez comment <span className="text-amber-500">désigner les gagnants</span>
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Plusieurs options s'offrent à vous selon vos objectifs et la réglementation
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {RESULT_TYPES.map((type, idx) => (
                <Card key={idx} className="border-2 hover:border-amber-300 transition-colors overflow-hidden">
                  <CardContent className="p-8">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center mb-6">
                      <type.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-800 mb-3">{type.title}</h3>
                    <p className="text-slate-600 mb-4">{type.description}</p>
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <p className="text-sm text-amber-700">
                        <strong>Idéal pour :</strong> {type.ideal}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Winner celebration image */}
            <div className="mt-16">
              <div className="relative rounded-2xl overflow-hidden shadow-xl">
                <img 
                  src={WINNER_IMAGE} 
                  alt="Célébration des gagnants" 
                  className="w-full h-64 md:h-80 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-transparent to-slate-900/80 flex items-center justify-center">
                  <div className="text-center text-white">
                    <Trophy className="w-16 h-16 mx-auto mb-4 text-yellow-400" />
                    <h3 className="text-2xl md:text-3xl font-bold">Une expérience mémorable pour vos gagnants</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Legal Section */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="mb-4 bg-slate-200 text-slate-700 border-slate-300">
                  <Scale className="w-3 h-3 mr-1" />
                  Aspect juridique
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                  Un règlement <span className="text-slate-600">conforme</span> à la législation
                </h2>
                <p className="text-lg text-slate-600 mb-8">
                  Nous rédigeons pour vous un règlement de jeu complet, conforme aux exigences légales françaises 
                  et déposé auprès d'un huissier de justice si nécessaire.
                </p>
                
                <div className="space-y-4">
                  {[
                    "Conditions de participation claires",
                    "Modalités de désignation des gagnants",
                    "Description des lots et leur valeur",
                    "Mentions légales obligatoires",
                    "Protection des données personnelles (RGPD)",
                    "Dépôt huissier possible"
                  ].map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                      <span className="text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-white rounded-2xl shadow-xl p-8 border">
                <div className="flex items-center gap-4 mb-6 pb-6 border-b">
                  <div className="w-14 h-14 rounded-xl bg-slate-100 flex items-center justify-center">
                    <FileText className="w-7 h-7 text-slate-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">Règlement type</h3>
                    <p className="text-slate-500">Exemple de structure</p>
                  </div>
                </div>
                
                <div className="space-y-3 text-sm">
                  {[
                    "Article 1 : Organisation du jeu",
                    "Article 2 : Conditions de participation",
                    "Article 3 : Modalités du jeu",
                    "Article 4 : Désignation des gagnants",
                    "Article 5 : Description des lots",
                    "Article 6 : Remise des lots",
                    "Article 7 : Données personnelles",
                    "Article 8 : Responsabilité",
                    "Article 9 : Acceptation du règlement"
                  ].map((article, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-slate-600 p-2 rounded hover:bg-slate-50">
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                      {article}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-orange-500 via-red-500 to-rose-600 text-white">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Sparkles className="w-16 h-16 mx-auto mb-6 text-yellow-300" />
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Prêt à créer votre jeu-concours ?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              Contactez-nous pour discuter de votre projet. Nous vous proposons une solution 
              100% personnalisée adaptée à vos objectifs et votre budget.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Button 
                size="lg"
                className="bg-white text-orange-600 hover:bg-white/90 rounded-full px-10 py-6 text-lg font-semibold"
                onClick={() => navigate('/site#contact')}
              >
                Demander un devis gratuit
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 rounded-full px-10 py-6 text-lg"
                onClick={() => navigate('/site')}
              >
                <Play className="w-5 h-5 mr-2" />
                Voir le Coffre MSC en action
              </Button>
            </div>
            
            <p className="mt-8 text-white/70 text-sm">
              Devis sur mesure • Accompagnement personnalisé • Solution clé en main
            </p>
          </div>
        </section>
      </div>
    </SharedLayout>
  );
};

export default GameContestServicePage;
